export interface SiteTerrainSurface {
  siteId: string;
  radiusM: number;
  size: number;
  /** Local tangent-frame heights relative to the supplied site anchor. */
  heights: number[];
  anchorEllipsoidM: number;
  equipmentGroundM?: number;
  buildings: Record<string, { baseM: number; groundM: number[] }>;
  source: string;
  sampledAt: string;
}

export function surfaceHeight(surface: SiteTerrainSurface, x: number, z: number): number | null {
  const n = surface.size,
    r = surface.radiusM;
  if (!Number.isFinite(x + z) || Math.abs(x) > r || Math.abs(z) > r) return null;
  const u = ((x + r) / (2 * r)) * (n - 1),
    v = ((z + r) / (2 * r)) * (n - 1);
  const i = Math.min(n - 2, Math.floor(u)),
    j = Math.min(n - 2, Math.floor(v));
  const a = u - i,
    b = v - j;
  const h = (row: number, col: number) => surface.heights[row * n + col];
  const value =
    h(j, i) * (1 - a) * (1 - b) +
    h(j, i + 1) * a * (1 - b) +
    h(j + 1, i) * (1 - a) * b +
    h(j + 1, i + 1) * a * b;
  return Number.isFinite(value) ? value : null;
}

/** Earth curvature in a local tangent frame; no vertical exaggeration. */
export function tangentHeight(heightM: number, anchorM: number, eastM: number, northM: number): number {
  return heightM - anchorM - (eastM * eastM + northM * northM) / (2 * 6371000);
}

export interface HorizonRay {
  azimuthDeg: number;
  samples: { distanceM: number; ellipsoidM: number }[];
}
export function horizonElevation(ray: HorizonRay, observerEllipsoidM: number): number {
  return Math.max(
    0,
    ...ray.samples
      .filter((s) => Number.isFinite(s.ellipsoidM) && s.distanceM > 0)
      .map(
        (s) =>
          (Math.atan2(tangentHeight(s.ellipsoidM, observerEllipsoidM, s.distanceM, 0), s.distanceM) * 180) /
          Math.PI,
      ),
  );
}

/** Conservative neighbouring-ray envelope; avoids inventing a narrow clear gap. */
export function horizonAt(azimuthDeg: number, horizon: number[]): number {
  if (!horizon.length) return 0;
  const index = ((((azimuthDeg % 360) + 360) % 360) / 360) * horizon.length;
  return Math.max(horizon[Math.floor(index) % horizon.length], horizon[Math.ceil(index) % horizon.length]);
}
